import React, { useState } from 'react';
import classNames from 'classnames';
import { Controller, RegisterOptions, useFormContext } from 'react-hook-form';
import StylishNewCreatableSelect from 'components/DesignSystems/New/StylishNewCreatableSelect';

type FormSelectFieldProps = {
  title: string;
  fieldName: string;
  rules?:
    | { [key: string]: { value: unknown; message: string } }
    | RegisterOptions;
  defaultOptions: { label: string; value: string }[] | undefined;
  placeholder?: string;
  variant?: 'full' | 'half';
  isMulti?: boolean;
  isClearable?: boolean;
  defaultValue?: any;
  style?: object;
  classes?: string;
  disabled?: boolean;
  onChangeCallback?: (value: string | string[] | undefined) => void;
};

const FormSelectField: React.FC<FormSelectFieldProps> = ({
  title,
  fieldName,
  rules,
  defaultOptions,
  placeholder,
  variant = 'full',
  isMulti = false,
  isClearable = false,
  defaultValue,
  style,
  classes,
  disabled = false,
  onChangeCallback,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const [options, setOptions] = useState(defaultOptions);

  const handleCreateOption = (inputValue: string) => {
    const newOption = {
      label: inputValue,
      value: inputValue.toLowerCase().replace(/\W/g, ''),
    };
    setOptions((prevOptions) => [...(prevOptions || []), newOption]);
  };

  const required = typeof rules?.required === 'object';

  return (
    <div
      className={classNames('mt-2', {
        'col-6': variant === 'half',
        'col-12': variant === 'full',
      })}
    >
      <label className="form-label">
        {title}
        {required ? (
          <span aria-label="Required field" className="required">
            *
          </span>
        ) : null}
      </label>
      <Controller
        control={control}
        name={fieldName}
        rules={rules}
        render={({ field: { onChange, onBlur, value } }) => (
          <StylishNewCreatableSelect
            classes={classes}
            styles={style}
            placeholder={placeholder}
            options={options || []}
            value={options?.filter((option) =>
              isMulti
                ? value
                    ?.map((v: { label: string; value: string }) => v.value)
                    .includes(option.value)
                : option.value === value?.value
            )}
            onChange={(selectedOption) => {
              const typedSelectedOption = selectedOption as
                | { label: string; value: string }
                | Array<{ label: string; value: string }>;
              const newValue = isMulti
                ? (
                    typedSelectedOption as Array<{
                      label: string;
                      value: string;
                    }>
                  ).map((option) => option.value)
                : (typedSelectedOption as { label: string; value: string })
                    ?.value || '';

              onChange(selectedOption);
              onChangeCallback?.(newValue);
            }}
            onCreateOption={(inputValue: string) => {
              handleCreateOption(inputValue);

              const newValue = isMulti
                ? [
                    ...(value || []),
                    inputValue.toLowerCase().replace(/\W/g, ''),
                  ]
                : inputValue.toLowerCase().replace(/\W/g, '');
              onChange({ value: newValue, label: inputValue });
              onChangeCallback?.(newValue);
            }}
            onBlur={onBlur}
            isMulti={isMulti}
            isClearable={isClearable}
            defaultValue={defaultValue}
            isDisabled={disabled}
          />
        )}
      />
      {errors[fieldName] ? (
        <div className="form-text form-error">
          {errors[fieldName].message as string}
        </div>
      ) : (
        <div className="form-text-none"></div>
      )}
    </div>
  );
};

export default FormSelectField;
